import { apiHttpClient } from '@/http/httpClient';
import type {
  Relation,
  RelationByAddressModel,
  RelationDropdownModel,
  RelationListModel,
  RelationListRequest, RelationOptionsRequest,
  RelationOutsourcingListRequest,
  RelationPreviewModel
} from '@/models/relation/relation';
import type { RelationImportResponse } from '@/models/relation/relation-import';
import type { PaginationItems } from '@/models/pagination';
import type { RelationAssignListRequest } from '@/models/relation/relation-assign-drawer';
import type { Address } from '@/models/relation/address';
import type { RelationGeneralInformation } from '@/models/relation/edit/general-information';
import type { RelationContactPerson } from '@/models/relation/edit/contact-persons';
import type { RelationAddress } from '@/models/relation/edit/address';
import type { CheckVatNumberRequest, CheckVatNumberResponse } from '@/models/relation/check-vat-number';
import type { CheckKvkNumberRequest, CheckKvkNumberResponse } from '@/models/relation/check-kvk-number';

const ENDPOINT_LIST_RELATION = "Relation/List";
const ENDPOINT_LIST_OUTSOURCING_RELATION = "Relation/OutsourcingList";
const ENDPOINT_GET_RELATION = "Relation/Get";
const ENDPOINT_GET_RELATION_PREVIEW = "Relation/GetPreview";
const ENDPOINT_GET_RELATION_BY_ADDRESS = "Relation/GetByAddress";
const ENDPOINT_UPDATE_RELATION = "Relation/Update";
const ENDPOINT_CREATE_RELATION = "Relation/Create";
const ENDPOINT_DELETE_RELATION = "Relation/Delete";
const ENDPOINT_LIST_RELATION_ASSIGN_OPTIONS = "Relation/ListAssignOptions";
const ENDPOINT_UPDATE_GENERAL_INFORMATION = "Relation/UpdateGeneralInformation";
const ENDPOINT_UPDATE_CONTACT_PERSON = "Relation/UpdateContactPerson";
const ENDPOINT_UPDATE_ADDRESS = "Relation/UpdateAddress";
const ENDPOINT_REMOVE_CONTACT_PERSON = "Relation/RemoveContactPerson";
const ENDPOINT_CHECK_VAT_NUMBER = "Relation/CheckVatNumber";
const ENDPOINT_CHECK_KVK_NUMBER = "Relation/CheckKvkNumber";
const ENDPOINT_GET_RELATION_SELECT_OPTIONS = "Relation/GetRelationOptions";
const ENDPOINT_IMPORT_RELATIONS = "Relation/Import";

export const listRelations = async (request: RelationListRequest) => {
    return await apiHttpClient().post<PaginationItems<RelationListModel>>(ENDPOINT_LIST_RELATION, request)
        .then(response => {
            return response;
        })
        .catch(error => {
            return error;
        })
}

export const listOutsourcingRelations = async (request: RelationOutsourcingListRequest) => {
    return await apiHttpClient().post<PaginationItems<RelationDropdownModel>>(ENDPOINT_LIST_OUTSOURCING_RELATION, request)
        .then(response => {
            return response;
        })
        .catch(error => {
            return error;
        })
}

export const getRelation = async (id: number) => {
    return await apiHttpClient().get<Relation>(`${ENDPOINT_GET_RELATION}/${id}`)
        .then(response => {
            return response;
        })
        .catch(error => {
            return error;
        })
}

export const getRelationPreview = async (id: number, addressId: number) => {
    return await apiHttpClient().get<RelationPreviewModel>(`${ENDPOINT_GET_RELATION_PREVIEW}/${id}/${addressId}`)
        .then(response => {
            return response;
        })
        .catch(error => {
            return error;
        })
}

export const getRelationByAddress = async (address: Address) => {
    const params = {
        zipCode: address.zipCode ?? '',
        houseNumber: address.houseNumber ?? '',
        houseNumberAddition: address.houseNumberAddition ?? '',
        street: address.street ?? '',
        country: address.country.toString()
    };

    return await apiHttpClient().get<RelationByAddressModel>(`${ENDPOINT_GET_RELATION_BY_ADDRESS}`, { params: params })
        .then(response => {
            return response;
        })
        .catch(error => {
            return error;
        })
}

export const createRelation = async (relation: Relation) => {
    return await apiHttpClient().post<Relation>(ENDPOINT_CREATE_RELATION, relation)
        .then(response => {
            return response;
        })
        .catch(error => {
            return error;
        })
}

export const updateRelation = async (relation: Relation) => {
    return await apiHttpClient().put(ENDPOINT_UPDATE_RELATION, relation)
        .then(response => {
            return response;
        })
        .catch(error => {
            return error;
        })
}

export const deleteRelation = async (id: number) => {
    return await apiHttpClient().delete(`${ENDPOINT_DELETE_RELATION}/${id}`, {
        params: {
            id: id
        }
    })
        .then(response => {
            return response;
        })
        .catch(error => {
            return error;
        })
}

export const listRelationAssignOptions = async (request: RelationAssignListRequest) => {
    return await apiHttpClient().post<PaginationItems<RelationListModel>>(ENDPOINT_LIST_RELATION_ASSIGN_OPTIONS, request)
        .then(response => {
            return response;
        })
        .catch(error => {
            return error;
        })
}

export const updateRelationGeneralInformation = async (request: RelationGeneralInformation) => {
    return await apiHttpClient().post(ENDPOINT_UPDATE_GENERAL_INFORMATION, request)
        .then(response => response)
        .catch(error => error);
}

export const updateRelationContactPerson = async (request: RelationContactPerson) => {
    return await apiHttpClient().post(ENDPOINT_UPDATE_CONTACT_PERSON, request)
        .then(response => response)
        .catch(error => error);
}

export const updateRelationAddress = async (request: RelationAddress) => {
    return await apiHttpClient().post(ENDPOINT_UPDATE_ADDRESS, request)
        .then(response => response)
        .catch(error => error);
}

export const removeContactPersonFromRelation = async (relationId: number, contactPersonId: number) => {
    return await apiHttpClient().delete(ENDPOINT_REMOVE_CONTACT_PERSON, {
        data: {
            relationId: relationId,
            contactPersonId: contactPersonId
        }
    })
        .then(response => response)
        .catch(error => error);
}

export const checkVatNumber = async (request: CheckVatNumberRequest) => {
    return await apiHttpClient().post<CheckVatNumberResponse>(ENDPOINT_CHECK_VAT_NUMBER, request)
        .then(response => {
            return response;
        })
        .catch(error => {
            return error;
        })
}

export const checkKvkNumber = async (request: CheckKvkNumberRequest) => {
    return await apiHttpClient().post<CheckKvkNumberResponse>(ENDPOINT_CHECK_KVK_NUMBER, request)
        .then(response => {
            return response;
        })
        .catch(error => {
            return error;
        })
}

export const getRelationSelectOptions = async (request: RelationOptionsRequest) => {
  return await apiHttpClient().post(ENDPOINT_GET_RELATION_SELECT_OPTIONS, request)
    .then(response => {
      return response;
    })
    .catch(error => {
      return error;
    })
}

export const importRelations = async (file: File) => {
  const formData = new FormData();
  formData.append("file", file);

  return await apiHttpClient(false).post<RelationImportResponse>(ENDPOINT_IMPORT_RELATIONS, formData)
    .then(response => {
      return response;
    })
    .catch(error => {
      return error;
    })
}
